import { useEffect, useRef, useState } from 'react';

interface UseTextFitOptions {
  minFontSize?: number;
  maxFontSize?: number;
  multiLine?: boolean;
  scale?: number; // Scaling factor (0-1) to apply to final font size
}

export const useTextFit = (
  text: string,
  options: UseTextFitOptions = {}
) => {
  const { minFontSize = 10, maxFontSize = 500, multiLine = true, scale = 1.0 } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useEffect(() => {
    const container = containerRef.current;
    const textElement = textRef.current;

    if (!container || !textElement) return;

    const fitText = () => {
      let min = minFontSize;
      let max = maxFontSize;
      let current = max;

      // Binary search for optimal font size
      while (min <= max) {
        current = Math.floor((min + max) / 2);
        textElement.style.fontSize = `${current}px`;

        const isOverflowing = 
          textElement.scrollHeight > container.clientHeight || 
          textElement.scrollWidth > container.clientWidth;

        if (isOverflowing) {
            max = current - 1;
        } else {
            min = current + 1;
        }
      }
      
      // Use the last safe size and apply scaling factor
      const scaledSize = Math.floor(max * scale);
      setFontSize(scaledSize);
    };

    const resizeObserver = new ResizeObserver(fitText);
    resizeObserver.observe(container);
    
    // Initial fit
    fitText();

    return () => resizeObserver.disconnect();
  }, [text, minFontSize, maxFontSize, multiLine, scale]);

  return { containerRef, textRef, fontSize };
};
